import { useNotificationCreatorBase } from './useNotificationCreatorBase.js'

/**
 * Composable for membership-related notifications
 */
export function useMembershipNotifications() {
  const {
    notificationCore,
    getMember,
    getSubKrewe,
    getActingUserInfo,
  } = useNotificationCreatorBase()

  /**
   * Create a notification for SubKrewe join request approval
   *
   * @param memberId ID of the member who was approved
   * @param subkreweId ID of the SubKrewe they joined
   * @param actingUserId ID of the captain/admin who approved the request
   * @returns ID of created notification
   */
  const createMembershipApprovedNotification = async (
    memberId: string,
    subkreweId: string,
    actingUserId: string,
  ): Promise<string> => {
    const member = await getMember(memberId)
    const subkrewe = getSubKrewe(subkreweId)

    // Get approver info
    const { name: approverName, type: approverType } = await getActingUserInfo(actingUserId)
    const approverInfo = `${approverName} (${approverType})`

    const title = `Your request to join ${subkrewe.name} was approved`
    const body = `Welcome to ${subkrewe.name}! Your request to join has been approved.\n\nApproved by: ${approverInfo}`

    return notificationCore.createNotificationForMember(memberId, {
      title,
      body,
      category: 'membership',
      priority: 'normal',
      linkType: 'subkrewe',
      linkTarget: subkreweId,
      emailScheduled: true,
      metadata: {
        emailTemplate: 'join-request-approved',
        emailData: {
          memberName: member.name,
          subkreweName: subkrewe.name,
          approverName,
          subkreweId,
        },
      },
    })
  }

  /**
   * Create a notification for SubKrewe join request denial
   *
   * @param memberId ID of the member who was denied
   * @param subkreweId ID of the SubKrewe they tried to join
   * @param actingUserId ID of the captain/admin who denied the request
   * @param reason Optional reason for denial
   * @returns ID of created notification
   */
  const createMembershipDeniedNotification = async (
    memberId: string,
    subkreweId: string,
    actingUserId: string,
    reason?: string,
  ): Promise<string> => {
    const member = await getMember(memberId)
    const subkrewe = getSubKrewe(subkreweId)

    // Get denier info
    const { name: denierName, type: denierType } = await getActingUserInfo(actingUserId)
    const denierInfo = `${denierName} (${denierType})`

    const title = `Your request to join ${subkrewe.name} was not approved`
    let body = `Your request to join ${subkrewe.name} was not approved.`

    if (reason) {
      body += `\n\nReason: ${reason}`
    }

    body += `\n\nResponse from: ${denierInfo}`

    return notificationCore.createNotificationForMember(memberId, {
      title,
      body,
      category: 'membership',
      priority: 'normal',
      linkType: 'none',
      emailScheduled: true,
      metadata: {
        emailTemplate: 'join-request-denied',
        emailData: {
          memberName: member.name,
          subkreweName: subkrewe.name,
          reason: reason || 'No reason provided',
          denierName,
        },
      },
    })
  }

  /**
   * Create a notification for SubKrewe join request approval
   *
   * @param memberId ID of the member who submitted the request
   * @param subkreweId ID of the SubKrewe they want to join
   * @param taskId ID of the join request task (for linking)
   * @returns Array of notification IDs (one per captain)
   */
  const createNewJoinRequestNotification = async (
    memberId: string,
    subkreweId: string,
    taskId: string,
  ): Promise<string[]> => {
    const member = await getMember(memberId)
    const subkrewe = getSubKrewe(subkreweId)

    const title = `New join request for ${subkrewe.name}`
    const body = `${member.name} has requested to join ${subkrewe.name}.`

    const notificationIds: string[] = []

    // Create notification for each captain
    if (subkrewe.captains && subkrewe.captains.length > 0) {
      for (const captain of subkrewe.captains) {
        if (captain.id) {
          const notificationId = await notificationCore.createNotificationForMember(captain.id, {
            title,
            body,
            category: 'membership',
            priority: 'normal',
            linkType: 'task',
            linkTarget: taskId,
            emailScheduled: true,
            metadata: {
              emailTemplate: 'new-join-request',
              emailData: {
                captainName: captain.name,
                memberName: member.name,
                subkreweName: subkrewe.name,
                memberId,
                subkreweId,
                taskId,
              },
            },
          })
          notificationIds.push(notificationId)
        }
      }
    }

    return notificationIds
  }

  /**
   * Notify a member they've been added to a SubKrewe directly (without a request)
   */
  const createAddedToSubKreweNotification = async (
    memberId: string,
    subkreweId: string,
    actingUserId: string,
  ): Promise<string> => {
    const member = await getMember(memberId)
    const subkrewe = getSubKrewe(subkreweId)

    // Get adder info
    const { name: adderName, type: adderType } = await getActingUserInfo(actingUserId)
    const adderInfo = `${adderName} (${adderType})`

    const title = `You've been added to ${subkrewe.name}`
    const body = `You have been added to ${subkrewe.name} by ${adderInfo}.`

    return notificationCore.createNotificationForMember(memberId, {
      title,
      body,
      category: 'membership',
      priority: 'normal',
      linkType: 'subkrewe',
      linkTarget: subkreweId,
      emailScheduled: true,
      metadata: {
        emailTemplate: 'added-to-subkrewe',
        emailData: {
          memberName: member.name,
          subkreweName: subkrewe.name,
          adderName,
          subkreweId,
        },
      },
    })
  }

  /**
   * Notify a member they've been removed from a SubKrewe
   */
  const createRemovedFromSubKreweNotification = async (
    memberId: string,
    subkreweName: string,
    actingUserId: string,
    reason?: string,
  ): Promise<string> => {
    const member = await getMember(memberId)

    // Get remover info
    const { name: removerName, type: removerType } = await getActingUserInfo(actingUserId)
    const removerInfo = `${removerName} (${removerType})`

    const title = `You've been removed from ${subkreweName}`
    let body = `You have been removed from ${subkreweName} by ${removerInfo}.`

    if (reason) {
      body += `\n\nReason: ${reason}`
    }

    return notificationCore.createNotificationForMember(memberId, {
      title,
      body,
      category: 'membership',
      priority: 'normal',
      linkType: 'none',
      emailScheduled: true,
      metadata: {
        emailTemplate: 'removed-from-subkrewe',
        emailData: {
          memberName: member.name,
          subkreweName,
          removerName,
          reason: reason || 'No reason provided',
        },
      },
    })
  }

  /**
   * Notify a member they left a SubKrewe themselves (softer copy)
   */
  const createSelfLeftSubKreweNotification = async (
    memberId: string,
    subkreweName: string,
  ): Promise<string> => {
    const member = await getMember(memberId)

    const title = `You left ${subkreweName}`
    const body = `You have left ${subkreweName}.`

    return notificationCore.createNotificationForMember(memberId, {
      title,
      body,
      category: 'membership',
      priority: 'normal',
      linkType: 'none',
      emailScheduled: true,
      metadata: {
        // 'left-subkrewe' was never a real template. Not `member-left-subkrewe`
        // either — that one is captain-facing ("Member Left {{subkreweName}}")
        // and this notification goes to the member who left.
        emailTemplate: 'notification',
        emailData: {
          memberName: member.name,
          subkreweName,
        },
      },
    })
  }

  /**
   * Notify a member they've been promoted to captain
   */
  const createPromotedToCaptainNotification = async (
    memberId: string,
    subkreweId: string,
    actingUserId: string,
  ): Promise<string> => {
    const member = await getMember(memberId)
    const subkrewe = getSubKrewe(subkreweId)

    // Get promoter info
    const { name: promoterName, type: promoterType } = await getActingUserInfo(actingUserId)
    const promoterInfo = `${promoterName} (${promoterType})`

    const title = `You've been promoted to Captain of ${subkrewe.name}`
    const body = `You have been promoted to Captain of ${subkrewe.name} by ${promoterInfo}. You can now approve member requests, edit SubKrewe information, and manage membership.`

    return notificationCore.createNotificationForMember(memberId, {
      title,
      body,
      category: 'membership',
      priority: 'high',
      linkType: 'subkrewe',
      linkTarget: subkreweId,
      emailScheduled: true,
      metadata: {
        emailTemplate: 'promoted-to-captain',
        emailData: {
          memberName: member.name,
          subkreweName: subkrewe.name,
          promoterName,
          subkreweId,
        },
      },
    })
  }

  /**
   * Notify captains when a member joins their SubKrewe directly
   */
  const createMemberJoinedNotificationForCaptains = async (
    memberId: string,
    subkreweId: string,
    actingUserId?: string,
  ): Promise<string[]> => {
    const member = await getMember(memberId)
    const subkrewe = getSubKrewe(subkreweId)

    const isRedshirt = String(member.member_type || '').toLowerCase() === 'redshirt'

    const title = `${isRedshirt ? '[Red Shirt] ' : ''}New member joined ${subkrewe.name}`
    const body = `${member.name}${isRedshirt ? ' (Red Shirt)' : ''} has joined your SubKrewe.`

    const notificationIds: string[] = []

    if (import.meta.dev) {
      console.warn(`[createMemberJoinedNotificationForCaptains] Acting user ID: ${actingUserId || 'none'}`)
    }

    // Create notification for each captain
    if (subkrewe.captains && subkrewe.captains.length > 0) {
      for (const captain of subkrewe.captains) {
        if (!captain.id) continue

        if (actingUserId === undefined || captain.id !== actingUserId) {
          const notificationId = await notificationCore.createNotificationForMember(captain.id, {
            title,
            body,
            category: 'membership',
            priority: 'normal',
            linkType: 'subkrewe',
            linkTarget: subkreweId,
            emailScheduled: true,
            metadata: {
              emailTemplate: 'member-joined-subkrewe',
              emailData: {
                captainName: captain.name,
                memberName: member.name,
                subkreweName: subkrewe.name,
                memberId,
                subkreweId,
                memberType: isRedshirt ? 'redshirt' : (member.member_type || undefined),
                isRedshirt,
              },
              memberType: isRedshirt ? 'redshirt' : (member.member_type || undefined),
              isRedshirt,
            },
          })
          notificationIds.push(notificationId)
        }
      }
    }

    return notificationIds
  }

  /**
   * Notify captains when a member leaves their SubKrewe
   * @param memberId ID of the member who left
   * @param subkreweId ID of the SubKrewe they left
   * @param actingUserId Optional ID of the user who performed the removal, to exclude them from notifications
   * @param reason Optional reason why the member left (e.g., "banned", "removed by captain", "voluntary")
   */
  const createMemberLeftNotificationForCaptains = async (
    memberId: string,
    subkreweId: string,
    actingUserId?: string,
    reason?: string,
  ): Promise<string[]> => {
    if (import.meta.dev) {
      console.warn('Reason in createMemberLeftNotificationForCaptains:', reason)
    }

    const member = await getMember(memberId)
    const subkrewe = getSubKrewe(subkreweId)

    const isRedshirt = String(member.member_type || '').toLowerCase() === 'redshirt'

    let title = `${isRedshirt ? '[Red Shirt] ' : ''}Member left ${subkrewe.name}`
    let body = `${member.name}${isRedshirt ? ' (Red Shirt)' : ''} has left your SubKrewe.`

    // Customize message based on reason
    if (reason === 'banned') {
      title = `${isRedshirt ? '[Red Shirt] ' : ''}Member banned from ${subkrewe.name}`
      body = `${member.name}${isRedshirt ? ' (Red Shirt)' : ''} has been banned and removed from your SubKrewe.`
    }
    else if (reason) {
      // For other reasons, add it to the message
      body = `${member.name}${isRedshirt ? ' (Red Shirt)' : ''} has left your SubKrewe. Reason: ${reason}`
    }

    const notificationIds: string[] = []

    if (import.meta.dev) {
      console.warn(`[createMemberLeftNotificationForCaptains] Acting user ID: ${actingUserId || 'none'}`)
    }

    // Create notification for each captain (excluding the acting user if provided)
    if (subkrewe.captains && subkrewe.captains.length > 0) {
      for (const captain of subkrewe.captains) {
        // Skip notification for the captain who performed the removal
        if (!captain.id) continue

        // Only send notification if this captain is not the one who performed the action
        if (actingUserId === undefined || captain.id !== actingUserId) {
          const notificationId = await notificationCore.createNotificationForMember(captain.id, {
            title,
            body,
            category: 'membership',
            priority: 'normal',
            linkType: 'subkrewe',
            linkTarget: subkreweId,
            emailScheduled: true,
            metadata: {
              emailTemplate: 'member-left-subkrewe',
              emailData: {
                captainName: captain.name,
                memberName: member.name,
                subkreweName: subkrewe.name,
                memberId,
                subkreweId,
                reason: reason || 'No specific reason provided',
                memberType: isRedshirt ? 'redshirt' : (member.member_type || undefined),
                isRedshirt,
              },
              memberType: isRedshirt ? 'redshirt' : (member.member_type || undefined),
              isRedshirt,
            },
          })
          notificationIds.push(notificationId)
        }
      }
    }

    return notificationIds
  }

  return {
    createMembershipApprovedNotification,
    createMembershipDeniedNotification,
    createNewJoinRequestNotification,
    createAddedToSubKreweNotification,
    createRemovedFromSubKreweNotification,
    createSelfLeftSubKreweNotification,
    createPromotedToCaptainNotification,
    createMemberJoinedNotificationForCaptains,
    createMemberLeftNotificationForCaptains,
  }
}
